/**
 * LoanCreateForm — Phase 1 manual entry sheet.
 * Sectioned: Client · Loan · Structure · Property · Commission · Notes.
 * Validates client-side then POSTs to /api/settled-loans.
 */
import React, { useState } from 'react';
import { X, Loader2, AlertCircle } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import { LENDERS, LENDER_GROUPS, isSpecialistLender } from '../../lib/lenders';

const ENTITY_TYPES = ['Individual', 'Joint', 'Company', 'Trust', 'SMSF'];
const PRODUCT_TYPES = ['Home Loan', 'Investment Loan', 'Construction', 'Commercial', 'Personal', 'SMSF Loan', 'Other'];
const REPAYMENT_TYPES = ['P&I', 'IO'];
const RATE_TYPES = ['Variable', 'Fixed', 'Split'];
const LOAN_PURPOSES = ['Owner-Occupied', 'Investment', 'Refinance', 'Construction', 'Top-Up', 'Commercial', 'Other'];

const Field = ({ label, required, children, className = '' }) => (
  <label className={`flex flex-col gap-1 text-sm ${className}`}>
    <span className="text-[11px] uppercase tracking-wider font-semibold text-slate-600 dark:text-slate-300">
      {label} {required && <span className="text-rose-500">*</span>}
    </span>
    {children}
  </label>
);

const baseInput = 'w-full px-3 py-2 rounded-lg bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-sm focus:outline-none focus:border-blue-500';

const LoanCreateForm = ({ onClose, onCreated }) => {
  const { token } = useAuth();
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const [saving, setSaving] = useState(false);

  // Defaults reflect the most common path so the CSM ticks the fewest boxes.
  const [f, setF] = useState({
    client_name: '',
    client_phone: '',
    client_email: '',
    entity_type: 'Individual',
    lender: '',
    loan_amount: '',
    settlement_date: new Date().toISOString().slice(0, 10),
    product_type: 'Home Loan',
    interest_rate: '',
    repayment_type: 'P&I',
    loan_term_years: 30,
    loan_purpose: 'Owner-Occupied',
    rate_type: 'Variable',
    fixed_rate_expiry: '',
    io_expiry_date: '',
    product_tier: 'Prime',
    originated_by_derwent: true,
    has_offset: false,
    offset_balance: '',
    has_redraw: false,
    property_value_at_settlement: '',
    property_address: '',
    expected_trail_rate_bps: '',
    expected_monthly_trail: '',
    clawback_end_date: '',
    notes: '',
  });

  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!f.client_name.trim()) return toast.error('Client name is required');
    if (!f.lender.trim()) return toast.error('Lender is required');
    if (!f.loan_amount || Number(f.loan_amount) <= 0) return toast.error('Loan amount must be > 0');
    if (!f.settlement_date) return toast.error('Settlement date is required');
    if (!f.interest_rate || Number(f.interest_rate) < 0) return toast.error('Interest rate is required');
    if ((f.rate_type === 'Fixed' || f.rate_type === 'Split') && !f.fixed_rate_expiry) {
      return toast.error('Fixed-rate expiry date is required when rate type is Fixed or Split');
    }
    if (f.repayment_type === 'IO' && !f.io_expiry_date) {
      return toast.error('IO expiry date is required when repayment is Interest-Only');
    }

    setSaving(true);
    try {
      const payload = {
        ...f,
        loan_amount: Number(f.loan_amount),
        interest_rate: Number(f.interest_rate),
        loan_term_years: Number(f.loan_term_years) || 30,
        offset_balance: f.offset_balance ? Number(f.offset_balance) : null,
        property_value_at_settlement: f.property_value_at_settlement ? Number(f.property_value_at_settlement) : null,
        expected_trail_rate_bps: f.expected_trail_rate_bps ? Number(f.expected_trail_rate_bps) : null,
        expected_monthly_trail: f.expected_monthly_trail ? Number(f.expected_monthly_trail) : null,
        fixed_rate_expiry: f.fixed_rate_expiry || null,
        clawback_end_date: f.clawback_end_date || null,
      };
      const r = await fetch(`${backendUrl}/api/settled-loans`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        // 409 Conflict — duplicate enrolment. The detail is a structured object.
        if (r.status === 409 && err?.detail?.code === 'duplicate_enrolment') {
          toast.error(err.detail.message, {
            description: `Existing record: ${err.detail.existing_loan_number}. Open it from the loan book instead of creating a duplicate.`,
            duration: 8000,
          });
          return;
        }
        const msg = typeof err.detail === 'string' ? err.detail : (err.detail?.message || 'Failed to create loan');
        throw new Error(msg);
      }
      const newLoan = await r.json();
      toast.success(`Loan ${newLoan.loan_number} created — DRAFT awaiting your approval`, {
        description: 'Open it from the loan book and tap Approve to start the care cadence.',
        duration: 6000,
      });
      onCreated(newLoan);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center bg-slate-950/60 backdrop-blur-md" data-testid="loan-create-form">
      <form
        onSubmit={submit}
        className="w-full sm:w-[640px] sm:max-w-[94vw] max-h-[92vh] overflow-y-auto bg-white dark:bg-slate-900 sm:rounded-3xl rounded-t-3xl border border-slate-200 dark:border-slate-700 shadow-2xl"
      >
        <div className="sticky top-0 bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 px-6 py-4 flex items-center justify-between z-10">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500">Post-Settlement</div>
            <h2 className="text-lg font-bold text-slate-900 dark:text-white">New Loan Record</h2>
          </div>
          <button type="button" onClick={onClose} aria-label="Close" className="p-2 rounded-full hover:bg-slate-100 dark:hover:bg-slate-800">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-6 py-5 space-y-6">
          {/* Client */}
          <Section title="Client">
            <Field label="Client name" required>
              <input className={baseInput} value={f.client_name} onChange={(e) => set('client_name', e.target.value)} data-testid="loan-client-name" />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Phone">
                <input className={baseInput} value={f.client_phone} onChange={(e) => set('client_phone', e.target.value)} placeholder="0412 345 678" />
              </Field>
              <Field label="Email">
                <input type="email" className={baseInput} value={f.client_email} onChange={(e) => set('client_email', e.target.value)} placeholder="client@example.com" />
              </Field>
            </div>
            <Field label="Entity type">
              <select className={baseInput} value={f.entity_type} onChange={(e) => set('entity_type', e.target.value)}>
                {ENTITY_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </Field>
          </Section>

          {/* Loan */}
          <Section title="Loan">
            <Field label="Lender" required>
              <select
                className={baseInput}
                value={LENDERS.some((l) => l.name === f.lender) ? f.lender : (f.lender ? '__other__' : '')}
                onChange={(e) => {
                  const v = e.target.value;
                  if (v === '__other__') set('lender', '');
                  else set('lender', v);
                }}
                data-testid="loan-lender"
              >
                <option value="">Select a lender…</option>
                {LENDER_GROUPS.map((g) => (
                  <optgroup key={g} label={g}>
                    {LENDERS.filter((l) => l.group === g).map((l) => (
                      <option key={l.name} value={l.name}>
                        {l.name}{l.specialist ? '  · triggers 6mo rate call' : ''}
                      </option>
                    ))}
                  </optgroup>
                ))}
                <option value="__other__">Other — type name below</option>
              </select>
              {!LENDERS.some((l) => l.name === f.lender) && (
                <input
                  className={`${baseInput} mt-2`}
                  value={f.lender}
                  onChange={(e) => set('lender', e.target.value)}
                  placeholder="Enter lender name"
                  data-testid="loan-lender-other"
                />
              )}
              {isSpecialistLender(f.lender) && (
                <div className="mt-1.5 flex items-start gap-1.5 text-[11px] text-amber-700 dark:text-amber-300" data-testid="lender-specialist-hint">
                  <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                  <span>
                    Specialist lender — the 6-month touchpoint will use the <span className="font-semibold font-mono">SPECIALIST_6M</span> rate-review email so we can look at moving them to a sharper rate once they&apos;ve built a track record.
                  </span>
                </div>
              )}
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Loan amount" required>
                <input type="number" min={0} step={1000} className={baseInput} value={f.loan_amount} onChange={(e) => set('loan_amount', e.target.value)} placeholder="750000" data-testid="loan-amount" />
              </Field>
              <Field label="Settlement date" required>
                <input type="date" className={baseInput} value={f.settlement_date} onChange={(e) => set('settlement_date', e.target.value)} data-testid="loan-settlement-date" />
              </Field>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Product type">
                <select className={baseInput} value={f.product_type} onChange={(e) => set('product_type', e.target.value)}>
                  {PRODUCT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </Field>
              <Field label="Purpose">
                <select className={baseInput} value={f.loan_purpose} onChange={(e) => set('loan_purpose', e.target.value)}>
                  {LOAN_PURPOSES.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </Field>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <Field label="Interest rate (%)" required>
                <input type="number" min={0} step="0.01" className={baseInput} value={f.interest_rate} onChange={(e) => set('interest_rate', e.target.value)} placeholder="5.89" data-testid="loan-rate" />
              </Field>
              <Field label="Repayment">
                <select className={baseInput} value={f.repayment_type} onChange={(e) => set('repayment_type', e.target.value)}>
                  {REPAYMENT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </Field>
              <Field label="Term (yrs)">
                <input type="number" min={1} max={40} className={baseInput} value={f.loan_term_years} onChange={(e) => set('loan_term_years', e.target.value)} />
              </Field>
            </div>
          </Section>

          {/* Structure */}
          <Section title="Structure">
            <Field label="Rate type">
              <select className={baseInput} value={f.rate_type} onChange={(e) => set('rate_type', e.target.value)} data-testid="loan-rate-type">
                {RATE_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </Field>
            {(f.rate_type === 'Fixed' || f.rate_type === 'Split') && (
              <Field label="Fixed-rate expiry date" required>
                <input type="date" className={baseInput} value={f.fixed_rate_expiry} onChange={(e) => set('fixed_rate_expiry', e.target.value)} data-testid="loan-fixed-expiry" />
              </Field>
            )}
            {f.repayment_type === 'IO' && (
              <Field label="IO expiry date" required>
                <input type="date" className={baseInput} value={f.io_expiry_date} onChange={(e) => set('io_expiry_date', e.target.value)} data-testid="loan-io-expiry" />
              </Field>
            )}
            <div className="grid grid-cols-2 gap-3">
              <Field label="Product tier">
                <select className={baseInput} value={f.product_tier} onChange={(e) => set('product_tier', e.target.value)} data-testid="loan-product-tier">
                  <option value="Prime">Prime</option>
                  <option value="NearPrime">Near-Prime</option>
                  <option value="Specialist">Specialist</option>
                </select>
              </Field>
              <label className="flex items-center gap-2 text-sm cursor-pointer mt-6">
                <input type="checkbox" checked={f.originated_by_derwent} onChange={(e) => set('originated_by_derwent', e.target.checked)} data-testid="loan-originated-by-derwent" />
                <span>Originated by Derwent</span>
              </label>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={f.has_offset} onChange={(e) => set('has_offset', e.target.checked)} />
                <span>Has offset</span>
              </label>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={f.has_redraw} onChange={(e) => set('has_redraw', e.target.checked)} />
                <span>Has redraw</span>
              </label>
            </div>
            {f.has_offset && (
              <Field label="Offset balance">
                <input type="number" min={0} step={100} className={baseInput} value={f.offset_balance} onChange={(e) => set('offset_balance', e.target.value)} placeholder="35000" />
              </Field>
            )}
          </Section>

          {/* Property */}
          <Section title="Property">
            <Field label="Property address">
              <input className={baseInput} value={f.property_address} onChange={(e) => set('property_address', e.target.value)} placeholder="12 Smith St, Hobart TAS 7000" />
            </Field>
            <Field label="Property value at settlement">
              <input type="number" min={0} step={1000} className={baseInput} value={f.property_value_at_settlement} onChange={(e) => set('property_value_at_settlement', e.target.value)} placeholder="950000" />
            </Field>
          </Section>

          {/* Commission */}
          <Section title="Commission">
            <div className="grid grid-cols-2 gap-3">
              <Field label="Expected trail (bps)">
                <input type="number" min={0} step="0.01" className={baseInput} value={f.expected_trail_rate_bps} onChange={(e) => set('expected_trail_rate_bps', e.target.value)} placeholder="15" />
              </Field>
              <Field label="Expected monthly trail ($)">
                <input type="number" min={0} step="0.01" className={baseInput} value={f.expected_monthly_trail} onChange={(e) => set('expected_monthly_trail', e.target.value)} placeholder="93.75" />
              </Field>
            </div>
            <Field label="Clawback end date">
              <input type="date" className={baseInput} value={f.clawback_end_date} onChange={(e) => set('clawback_end_date', e.target.value)} />
            </Field>
          </Section>

          {/* Notes */}
          <Section title="Notes">
            <Field label="Initial file note">
              <textarea rows={3} className={baseInput} value={f.notes} onChange={(e) => set('notes', e.target.value)} placeholder="Anything to flag at settlement…" />
            </Field>
          </Section>
        </div>

        <div className="sticky bottom-0 bg-white dark:bg-slate-900 border-t border-slate-200 dark:border-slate-800 px-6 py-3 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-full text-sm font-semibold text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            className="inline-flex items-center gap-1.5 px-5 py-2 rounded-full text-sm font-semibold bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-60"
            data-testid="loan-create-submit"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
            Create loan record
          </button>
        </div>
      </form>
    </div>
  );
};

const Section = ({ title, children }) => (
  <div>
    <h3 className="text-[10px] uppercase tracking-[0.22em] font-bold text-blue-600 mb-2.5">{title}</h3>
    <div className="space-y-3">{children}</div>
  </div>
);

export default LoanCreateForm;
